import { Input, Label, DropdownOptions, fieldClassName, labelClassName, errorTextClassName } from '@shared/ui';
import { isSelectableRegion } from '@shared/hooks/useCountryRegionOptions';

const buildTestId = (dataTestId, suffix) => (dataTestId ? `${dataTestId}-${suffix}` : undefined);

// The base <Input> has `disabled:pointer-events-none`, which also suppresses its own
// `disabled:cursor-not-allowed` — so a locked field (shipping while "Same as billing address" is
// ticked, the mirrored Country) looked editable on hover. Re-enabling pointer events on the
// disabled state lets the not-allowed cursor show; a disabled input still can't be focused or typed in.
const inputClassName = `${fieldClassName} disabled:pointer-events-auto disabled:cursor-not-allowed`;

// Region/State is optional everywhere, so once a country has real regions the dropdown offers an
// explicit "none" choice instead of forcing one.
export const NO_REGION_SELECTION_LABEL = 'No region/state';

/**
 * Address Line 1 / 2 — the free-text, full-width part of one address block. Every address form reads:
 *
 *   Address Line 1
 *   Address Line 2
 *   City | Region/State | Post/ZIP Code     (`CityField`, `RegionSelect`, `PostalCodeField`)
 *   Country                                  (`CountrySelect`, a third of the width)
 *
 * The other fields are deliberately separate components so each call site lays out that grid itself:
 * Country/Region are shared across billing+shipping (a HaloPSA Site only has one country/region
 * pair), so a page renders one of each per address rather than a monolithic "address form" that
 * assumes each address owns its own country. `showLine3` (City under Line 2) remains for callers
 * not yet on that grid.
 */
export function AddressLines({
  namePrefix = '',
  register,
  errors,
  showLine3 = false,
  disabled = false,
  dataTestId,
}) {
  const tid = (suffix) => buildTestId(dataTestId, suffix);
  const fieldName = (name) => (namePrefix ? `${namePrefix}${name.charAt(0).toUpperCase()}${name.slice(1)}` : name);
  const fieldError = (name) => errors?.[fieldName(name)];

  return (
    <div className="space-y-4" data-testid={dataTestId}>
      <div className="space-y-2" data-testid={tid('address1-section')}>
        <Label htmlFor={fieldName('address1')} className={labelClassName} data-testid={tid('address1-label')}>
          Address Line 1
        </Label>
        <Input
          id={fieldName('address1')}
          {...register(fieldName('address1'))}
          disabled={disabled}
          placeholder="e.g. Apartment 4B, Suite 200"
          className={`${inputClassName} ${fieldError('address1') ? 'border-danger-fg' : ''}`}
          data-testid={tid('address1-input')}
        />
        {fieldError('address1') && (
          <span className={errorTextClassName} data-testid={tid('address1-error')}>{fieldError('address1').message}</span>
        )}
      </div>

      <div className="space-y-2" data-testid={tid('address2-section')}>
        <Label htmlFor={fieldName('address2')} className={labelClassName} data-testid={tid('address2-label')}>Address Line 2</Label>
        <Input
          id={fieldName('address2')}
          {...register(fieldName('address2'))}
          disabled={disabled}
          placeholder="e.g. 123 Main Street (optional)"
          className={inputClassName}
          data-testid={tid('address2-input')}
        />
        {fieldError('address2') && (
          <span className={errorTextClassName} data-testid={tid('address2-error')}>{fieldError('address2').message}</span>
        )}
      </div>

      {showLine3 && (
        <CityField namePrefix={namePrefix} register={register} errors={errors} disabled={disabled} dataTestId={dataTestId} />
      )}
    </div>
  );
}

/**
 * City — HaloPSA's Address Line 3 (`address3` / `shippingAddress3`), so its field name and test ids
 * stay `address3`. The first cell of the City | Region/State | Post/ZIP Code row.
 */
export function CityField({
  namePrefix = '',
  register,
  errors,
  disabled = false,
  dataTestId,
}) {
  const tid = (suffix) => buildTestId(dataTestId, suffix);
  const name = namePrefix ? `${namePrefix}Address3` : 'address3';
  const fieldError = errors?.[name];

  return (
    <div className="space-y-2" data-testid={tid('address3-section')}>
      <Label htmlFor={name} className={labelClassName} data-testid={tid('address3-label')}>City</Label>
      <Input
        id={name}
        {...register(name)}
        disabled={disabled}
        placeholder="e.g. Springfield"
        className={`${inputClassName} ${fieldError ? 'border-danger-fg' : ''}`}
        data-testid={tid('address3-input')}
      />
      {fieldError && (
        <span className={errorTextClassName} data-testid={tid('address3-error')}>{fieldError.message}</span>
      )}
    </div>
  );
}

/**
 * The Postal/Zip Code field on its own — the last cell of the City | Region/State | Post/ZIP Code
 * row under <AddressLines> (see the layout there).
 */
export function PostalCodeField({
  namePrefix = '',
  register,
  errors,
  label = 'Postal Code',
  disabled = false,
  dataTestId,
}) {
  const tid = (suffix) => buildTestId(dataTestId, suffix);
  const fieldName = (name) => (namePrefix ? `${namePrefix}${name.charAt(0).toUpperCase()}${name.slice(1)}` : name);
  const fieldError = errors?.[fieldName('postalCode')];

  return (
    <div className="space-y-2" data-testid={tid('postal-code-section')}>
      <Label htmlFor={fieldName('postalCode')} className={labelClassName} data-testid={tid('postal-code-label')}>
        {label}
      </Label>
      <Input
        id={fieldName('postalCode')}
        {...register(fieldName('postalCode'))}
        disabled={disabled}
        placeholder="Enter postal code"
        className={inputClassName}
        data-testid={tid('postal-code-input')}
      />
      {fieldError && (
        <span className={errorTextClassName} data-testid={tid('postal-code-error')}>{fieldError.message}</span>
      )}
    </div>
  );
}

/**
 * One Country dropdown. `mirrored` renders it as a disabled, read-only echo of `value` with a hint
 * (used for the shipping card, since country is shared with billing) instead of its own editable
 * control.
 */
export function CountrySelect({
  id = 'country',
  label = 'Country',
  value,
  onValueChange,
  options,
  loading,
  disabled = false,
  error,
  mirrored = false,
  mirroredHint = 'Country is shared with your billing address.',
  dataTestId,
}) {
  if (mirrored) {
    const selected = options?.find((o) => o.value === value);
    return (
      <div className="space-y-2" data-testid={dataTestId}>
        <Label htmlFor={id} className={labelClassName} data-testid={`${dataTestId}-label`}>{label}</Label>
        <Input
          id={id}
          value={selected?.label || ''}
          disabled
          readOnly
          className={`${inputClassName} text-fg-disabled`}
          data-testid={`${dataTestId}-input`}
        />
        <p className="text-xs text-fg-muted" data-testid={`${dataTestId}-hint`}>{mirroredHint}</p>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid={dataTestId}>
      <Label htmlFor={id} className={labelClassName} data-testid={`${dataTestId}-label`}>
        {label}
      </Label>
      <DropdownOptions
        id={id}
        value={value}
        onValueChange={onValueChange}
        options={options}
        loading={loading}
        disabled={disabled}
        placeholder="Select a country"
        error={error}
        dataTestId={`${dataTestId}-dropdown`}
      />
    </div>
  );
}

/**
 * One Region/State dropdown, sourced from useCountryRegionOptions(sharedCountryId). Each address
 * block (billing, shipping) renders its own RegionSelect with its own `value`/`onValueChange`, but
 * both read `options` from the same hook call so they always reflect the one shared country.
 */
export function RegionSelect({
  id = 'region',
  label = 'Region/State',
  value,
  onValueChange,
  options,
  loading,
  disabled = false,
  error,
  // Forwarded to DropdownOptions; omitted, the dropdown keeps its default error size.
  errorClassName,
  dataTestId,
}) {
  // Only offered once the list holds real regions — a country with none already gets the
  // "No region available" sentinel option, and a second "none" entry beside it would be noise.
  const canClear = !loading && !!options?.some(isSelectableRegion);

  return (
    <div className="space-y-2" data-testid={dataTestId}>
      <Label htmlFor={id} className={labelClassName} data-testid={`${dataTestId}-label`}>{label}</Label>
      <DropdownOptions
        id={id}
        value={value}
        onValueChange={onValueChange}
        options={options}
        loading={loading}
        disabled={disabled || !options?.length}
        placeholder={loading ? 'Loading regions...' : 'Select region (optional)'}
        placeholderWhenEmpty
        allowEmpty={canClear}
        emptyLabel={NO_REGION_SELECTION_LABEL}
        error={error}
        errorClassName={errorClassName}
        dataTestId={`${dataTestId}-dropdown`}
      />
    </div>
  );
}
